/**
 * Filter Dialog Component (Reports v2)
 *
 * Quick-filter overlay for the report detail view. Lists existing
 * ReportFilter rows for the dashboard's first indicator and lets the
 * user add or remove filters. Each row is { field, operator, value }.
 *
 *   - Field choices come from /reporting/builder/dsid-catalog/
 *   - Operator choices come from /reporting/builder/operator-catalog/
 *     (grouped by the field's data_type)
 *   - Filter rows are persisted to /reporting/filters/?indicator={id}
 *
 * On "Apply" the dialog closes and the parent should refresh the report
 * data.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  reportingAPI,
  type BuilderFieldDef,
  type FieldDataType,
  type OperatorEntry,
  type ReportFilter,
  type ReportFilterCreatePayload,
  type ReportFilterValue,
} from '../../../lib/api/reporting';

interface FilterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The indicator the filter rows are scoped to. The parent should pick
   * the dashboard's first indicator (id) for the quick-filter overlay.
   */
  indicatorId: number | null;
  /**
   * Called once after the user clicks Apply. The parent should refresh
   * the report's chart data.
   */
  onApplied: () => void;
}

type OperatorGroupKey = 'text' | 'number' | 'enum' | 'bool' | 'date' | 'datetime';

// Map field.data_type -> the key used in the operator catalog response
function operatorGroupForDataType(dataType?: FieldDataType): OperatorGroupKey {
  switch (dataType) {
    case 'int':
    case 'decimal':
      return 'number';
    case 'date':
      return 'date';
    case 'datetime':
      return 'datetime';
    case 'enum':
      return 'enum';
    case 'bool':
      return 'bool';
    case 'string':
    default:
      return 'text';
  }
}

interface FieldOption {
  dsid: number;
  field_id: number;
  label: string;
  dataset_label: string;
  field: BuilderFieldDef;
}

export function FilterDialog({ open, onOpenChange, indicatorId, onApplied }: FilterDialogProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState<ReportFilter[]>([]);
  const [fieldOptions, setFieldOptions] = useState<FieldOption[]>([]);
  const [operators, setOperators] = useState<Partial<Record<OperatorGroupKey, OperatorEntry[]>>>({});
  const [touched, setTouched] = useState(false);

  // New row state
  const [newFieldKey, setNewFieldKey] = useState<string>(''); // "dsid:field_id"
  const [newOperator, setNewOperator] = useState<string>('');
  const [newValue, setNewValue] = useState<string>('');

  const loadAll = useCallback(async () => {
    if (!indicatorId) return;
    setLoading(true);
    try {
      const [filtersRes, catalogRes, opsRes] = await Promise.all([
        reportingAPI.listFilters(indicatorId),
        reportingAPI.getDsidCatalog(),
        reportingAPI.getOperatorCatalog(),
      ]);

      setFilters(filtersRes.data.results || []);

      const opts: FieldOption[] = [];
      (catalogRes.data.datasets || []).forEach((ds) => {
        (ds.fields || []).forEach((f) => {
          if (f.is_filterable === false) return;
          opts.push({
            dsid: ds.dsid,
            field_id: f.field_id,
            label: f.label,
            dataset_label: ds.label,
            field: f,
          });
        });
      });
      setFieldOptions(opts);

      setOperators(opsRes.data);
    } catch (err: unknown) {
      console.error('Failed to load filter editor data:', err);
      toast.error('Failed to load filter editor');
    } finally {
      setLoading(false);
    }
  }, [indicatorId]);

  useEffect(() => {
    if (open) {
      setTouched(false);
      setNewFieldKey('');
      setNewOperator('');
      setNewValue('');
      void loadAll();
    }
  }, [open, loadAll]);

  // Operators for the field currently selected in the "add" row
  const selectedField = useMemo(() => {
    if (!newFieldKey) return null;
    const [dsid, fid] = newFieldKey.split(':').map((s) => parseInt(s));
    return fieldOptions.find((f) => f.dsid === dsid && f.field_id === fid) || null;
  }, [newFieldKey, fieldOptions]);

  const operatorsForField: OperatorEntry[] = useMemo(() => {
    if (!selectedField) return [];
    const group = operatorGroupForDataType(selectedField.field.data_type);
    return operators[group] || [];
  }, [selectedField, operators]);

  // Reset operator when field changes if no longer valid
  useEffect(() => {
    if (!operatorsForField.find((o) => o.key === newOperator)) {
      setNewOperator(operatorsForField[0]?.key || '');
    }
  }, [operatorsForField, newOperator]);

  const buildValue = (operator: string, raw: string): ReportFilterValue => {
    // Most operators accept a single value string. is_empty / is_not_empty /
    // blank operators ignore the value but we send {} to keep the API tidy.
    const valuelessOperators = new Set(['is_empty', 'is_not_empty', 'blank']);
    if (valuelessOperators.has(operator)) return {};
    return { value: raw };
  };

  const handleAddFilter = async () => {
    if (!indicatorId || !selectedField || !newOperator) return;
    setSaving(true);
    try {
      const payload: ReportFilterCreatePayload = {
        indicator: indicatorId,
        group: null,
        field_dsid: selectedField.dsid,
        field_id: selectedField.field_id,
        field_data_type: 0,
        operator: newOperator,
        value: buildValue(newOperator, newValue),
        date_from_today: false,
        shifts: '',
        sort_order: filters.length,
      };
      const response = await reportingAPI.createFilter(payload);
      setFilters((prev) => [...prev, response.data]);
      setTouched(true);
      setNewValue('');
      toast.success('Filter added');
    } catch (err: unknown) {
      console.error('Failed to add filter:', err);
      toast.error('Failed to add filter');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveFilter = async (filter: ReportFilter) => {
    try {
      await reportingAPI.deleteFilter(filter.id);
      setFilters((prev) => prev.filter((f) => f.id !== filter.id));
      setTouched(true);
      toast.success('Filter removed');
    } catch (err: unknown) {
      console.error('Failed to delete filter:', err);
      toast.error('Failed to remove filter');
    }
  };

  const handleApply = () => {
    if (touched) onApplied();
    onOpenChange(false);
  };

  const fieldLabelLookup = useMemo(() => {
    const map = new Map<string, FieldOption>();
    fieldOptions.forEach((f) => map.set(`${f.dsid}:${f.field_id}`, f));
    return map;
  }, [fieldOptions]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Quick Filters</DialogTitle>
          <DialogDescription>
            Restrict this report's data using one or more filters. Filters apply to
            the first chart on the report.
          </DialogDescription>
        </DialogHeader>

        {!indicatorId ? (
          <div className="py-6 text-sm text-muted-foreground text-center">
            This report has no charts to filter yet. Add a chart first.
          </div>
        ) : loading ? (
          <div className="py-8 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            Loading filters...
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Existing filters */}
            <div className="space-y-2">
              <Label>Active Filters</Label>
              {filters.length === 0 ? (
                <div className="text-sm text-muted-foreground py-3 px-3 border rounded-md">
                  No filters applied.
                </div>
              ) : (
                <div className="space-y-2">
                  {filters.map((f) => {
                    const opt = fieldLabelLookup.get(`${f.field_dsid}:${f.field_id}`);
                    const display = opt
                      ? `${opt.dataset_label} → ${opt.label}`
                      : `DSID ${f.field_dsid} • Field ${f.field_id}`;
                    const valueDisplay =
                      f.value && Object.keys(f.value).length > 0
                        ? JSON.stringify(f.value.value ?? f.value)
                        : '';
                    return (
                      <div
                        key={f.id}
                        className="flex items-center gap-2 p-2 border rounded-md"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{display}</div>
                          <div className="text-xs text-muted-foreground">
                            {f.operator} {valueDisplay && `• ${valueDisplay}`}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => void handleRemoveFilter(f)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Add new filter */}
            <div className="space-y-2 pt-2 border-t">
              <Label>Add Filter</Label>
              <div className="grid grid-cols-[2fr_1fr_1.5fr_auto] gap-2">
                <Select value={newFieldKey} onValueChange={setNewFieldKey}>
                  <SelectTrigger>
                    <SelectValue placeholder="Field" />
                  </SelectTrigger>
                  <SelectContent>
                    {fieldOptions.map((opt) => (
                      <SelectItem
                        key={`${opt.dsid}:${opt.field_id}`}
                        value={`${opt.dsid}:${opt.field_id}`}
                      >
                        {opt.dataset_label} → {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={newOperator}
                  onValueChange={setNewOperator}
                  disabled={!selectedField}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Operator" />
                  </SelectTrigger>
                  <SelectContent>
                    {operatorsForField.map((op) => (
                      <SelectItem key={op.key} value={op.key}>
                        {op.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Input
                  placeholder="Value"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  disabled={!selectedField || !newOperator}
                />

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void handleAddFilter()}
                  disabled={!selectedField || !newOperator || saving}
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {selectedField && (
                <p className="text-xs text-muted-foreground">
                  {selectedField.dataset_label} • {selectedField.field.data_type}
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply}>Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
